import React, { useState } from "react";
import classnames from "classnames";
import { useSelector } from "react-redux";

import { getPromotions } from "api/requests/getPromotions";
import { Action, Category } from "constants/events";
import { Promotion } from "custom-types/Promotions";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import { useEventTracker } from "hooks/useEventTracker";
import { getCoordinates } from "redux/selectors/location";
import { getUserId } from "redux/selectors/user";
import { getDispensaryUrl } from "utils/dispensaryUrls";
import { getRoundedDistance } from "utils/distance";

import DealCard from "components/botanic//DealCard";
import Button from "components/botanic/Button";

type Props = {
  className?: string;
  promotionsProp?: Promotion[];
};

const PROMOS_PER_CLICK = 6;

const PromoResults: React.FC<Props> = ({ className, promotionsProp }) => {
  const countryCode = useDomainCountryCode();
  const coordinates = useSelector(getCoordinates);
  const isCanada = countryCode === "CA";
  const userId = useSelector(getUserId);
  const { publishEvent } = useEventTracker();

  const [promotions, setPromotions] = useState(promotionsProp || []);
  const [visiblePromotionsCount, setVisiblePromotionsCount] =
    useState(PROMOS_PER_CLICK);
  const showLoadMore = promotions.length > visiblePromotionsCount;

  const handleSeeMoreClick = async () => {
    if (!showLoadMore) {
      return;
    }
    publishEvent({
      action: Action.click,
      category: Category.loadMorePromotionsButton,
      label: "",
    });
    const { promotions: newPromos } = await getPromotions(
      coordinates,
      countryCode,
      promotions.length,
      PROMOS_PER_CLICK
    );

    setPromotions(promotions.concat(newPromos));
    setVisiblePromotionsCount(visiblePromotionsCount + PROMOS_PER_CLICK);
  };

  return (
    <div className={classnames(className)} data-testid="dispensary-promotions">
      <h2 className="heading--l">
        {isCanada ? "Retail store promotions" : "Dispensary promotions"}
      </h2>
      <h3 className="heading--m font-bold mb-lg">
        Know before you go! These promos are available to claim in-store. Ask
        your budtender before you checkout!
      </h3>

      <div className="row -mb-md">
        {promotions.slice(0, visiblePromotionsCount).map((promo) => {
          const {
            dispensary: { id: dispensaryId, slug, distanceMi },
          } = promo;

          const promoUrl = getDispensaryUrl({
            countryCode,
            slug,
            tab: "deals",
          });
          return (
            <div key={dispensaryId} className="col md:col-1/3 mb-md">
              <DealCard
                data-testid="promo-deal-card"
                deal={
                  {
                    ...promo,
                    dealUrl: promoUrl,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
                  } as any
                }
                showFulfillment={true}
                countryCode={countryCode}
                className="rounded h-full overflow-hidden elevation-low"
                onClick={() =>
                  publishEvent({
                    action: Action.click,
                    category: Category.dealsMenuBrowsePromotions,
                    dispensaryDistance: getRoundedDistance(
                      distanceMi,
                      countryCode
                    ),
                    dispensaryId,
                    label: promo.title.toLowerCase(),
                    userId: userId as number,
                  })
                }
                onLoad={() =>
                  publishEvent({
                    action: Action.impression,
                    category: Category.dealsMenuBrowsePromotions,
                    dispensaryDistance: getRoundedDistance(
                      distanceMi,
                      countryCode
                    ),
                    dispensaryId,
                    label: promo.title.toLowerCase(),
                    nonInteractionEvent: true,
                    userId: userId as number,
                  })
                }
              />
            </div>
          );
        })}
      </div>

      {showLoadMore && (
        <Button
          data-testid="load-more-promos"
          onClick={handleSeeMoreClick}
          className="mt-xxl"
        >
          load more promotions
        </Button>
      )}
    </div>
  );
};

export default PromoResults;
